import React, { Component } from 'react';
import { connect } from 'react-redux';
import classnames from 'classnames';

import { Row, Col, Button } from 'reactstrap';

import { callApi, formatDateTime } from '../../utils';
import { showError } from '../../actions/notificationActions';

import DataTable, { PageHeader, EmptyState } from '../DataTable';
import FilterBuilder, { buildWhere } from '../FilterBuilder';
import FilterPanel, { whereToConditions } from '../SavedFilters/FilterPanel';
import { createSavedFilter } from '../../services/savedFiltersApi';
import EventModal from './EventModal';

const STATUS_OPTIONS = ['SUCCESSFUL', 'FAILED', 'PENDING'];

const PROVIDER_EVENT_FIELDS = [
  { key: 'reference', label: 'Reference', type: 'string' },
  { key: 'provider_key', label: 'Provider Key', type: 'string' },
  { key: 'event_type', label: 'Event Type', type: 'string' },
  { key: 'transmission_count', label: 'Transmissions', type: 'number' },
  { key: 'status', label: 'Status', type: 'enum', options: STATUS_OPTIONS },
  { key: 'created_at', label: 'Date', type: 'date' },
];

function encodeConditions(conditions) {
  return encodeURIComponent(
    JSON.stringify(conditions.map(({ id, ...rest }) => rest))
  );
}

function parseUrlParams(search) {
  const params = new URLSearchParams(search);
  let conditions = [];
  let logicalOp = '$and';
  let page = 1;

  const raw = params.get('c');
  if (raw) {
    try {
      const parsed = JSON.parse(decodeURIComponent(raw));
      conditions = parsed.map((c, i) => ({ ...c, id: Date.now() + i }));
    } catch (_) {}
  }

  const op = params.get('op');
  if (op === '$and' || op === '$or') logicalOp = op;

  const p = parseInt(params.get('page'), 10);
  if (p > 0) page = p;

  return { conditions, logicalOp, page };
}

class List extends Component {
  constructor(props) {
    super(props);
    const { conditions, logicalOp, page } = parseUrlParams(
      props.location ? props.location.search : ''
    );
    this.state = {
      rows: [],
      conditions,
      logicalOp,
      page,
      limit: 30,
      loading: false,
      pages: 1,
      showModal: false,
      currentRow: null,
      activeSavedFilter: null,
      builderKey: 0,
    };
  }

  componentDidMount() {
    if (this.state.conditions.length > 0) {
      this.load();
    }
  }

  startLoad = () => this.setState({ loading: true });
  stopLoad = () => this.setState({ loading: false });
  scrollToTop = () => setTimeout(() => window.scrollTo(0, 0), 200);

  syncUrl = (conditions, logicalOp, page) => {
    const params = new URLSearchParams();
    if (conditions.length > 0) {
      params.set('c', encodeConditions(conditions));
      params.set('op', logicalOp);
    }
    if (page > 1) params.set('page', String(page));
    const search = params.toString();
    this.props.history.replace({ search: search ? `?${search}` : '' });
  };

  load = () => {
    const { conditions, logicalOp, page, limit } = this.state;
    const where = buildWhere(conditions, logicalOp);
    const hasFilter = Object.keys(where).length > 0;

    this.startLoad();
    callApi('/provider-events', {
      ...(hasFilter ? { where } : {}),
      limit,
      page,
    })
      .then(({ data = [], meta = {} }) => {
        this.setState({ loading: false, rows: data, pages: meta.pages });
        this.scrollToTop();
      })
      .catch((err) => {
        this.stopLoad();
        this.props.dispatch(showError(err));
      });
  };

  setPage = (page) => {
    if (page) {
      this.setState({ page }, () => {
        this.syncUrl(this.state.conditions, this.state.logicalOp, page);
        this.load();
      });
    }
  };

  clearFilters = () => {
    this.setState(
      {
        conditions: [],
        logicalOp: '$and',
        page: 1,
        activeSavedFilter: null,
        rows: [],
      },
      () => this.syncUrl([], '$and', 1)
    );
  };

  view = (record) => {
    callApi(`/provider-events/${record.id}`)
      .then(() => {
        this.props.history.push(`/provider-events/${record.id}`);
      })
      .catch((err) => {
        console.error(err);
      });
  };

  handleFilterChange = (_where, conditions, logicalOp) => {
    this.setState(
      { conditions, logicalOp, page: 1, activeSavedFilter: null },
      () => {
        this.syncUrl(conditions, logicalOp, 1);
        if (conditions.length > 0) {
          this.load();
        } else {
          this.setState({ rows: [] });
        }
      }
    );
  };

  handleSaveFilter = ({ label, description }) => {
    const { conditions, logicalOp } = this.state;
    const where = buildWhere(conditions, logicalOp);
    if (Object.keys(where).length === 0) return;
    createSavedFilter('provider-events', {
      type: 'custom',
      label,
      description,
      value: JSON.stringify(where),
    })
      .then(({ data }) => {
        this.setState({ activeSavedFilter: data });
      })
      .catch((err) => {
        this.props.dispatch(showError(err));
      });
  };

  handleApplySavedFilter = (filter) => {
    const parsed = whereToConditions(filter.value, PROVIDER_EVENT_FIELDS);
    const conditions = parsed.conditions.map((c, i) => ({
      ...c,
      id: Date.now() + i,
    }));
    const logicalOp = parsed.logicalOp;
    this.setState(
      function (prev) {
        return {
          conditions,
          logicalOp,
          activeSavedFilter: filter,
          page: 1,
          builderKey: prev.builderKey + 1,
        };
      },
      () => {
        this.syncUrl(conditions, logicalOp, 1);
        if (conditions.length > 0) this.load();
      }
    );
  };

  handleSavedFilterUpdated = (filter) => {
    this.setState(function (prev) {
      var active = prev.activeSavedFilter;
      return {
        activeSavedFilter: active && active.id === filter.id ? filter : active,
      };
    });
  };

  handleDeletedSavedFilter = (filterId) => {
    this.setState(function (prev) {
      var active = prev.activeSavedFilter;
      return {
        activeSavedFilter: active && active.id === filterId ? null : active,
      };
    });
  };

  render() {
    const {
      rows,
      page,
      loading,
      limit,
      pages,
      showModal,
      currentRow,
      conditions,
      logicalOp,
      activeSavedFilter,
      builderKey,
    } = this.state;

    const hasFilter = conditions.length > 0;
    const hasResults = rows.length > 0;

    return (
      <div className="container-fluid">
        <EventModal
          show={showModal}
          data={currentRow}
          toggle={() => this.setState({ showModal: false, currentRow: null })}
          dispatch={this.props.dispatch}
        />

        <Row className="mt-3 mt-md-4">
          <Col xs={12}>
            {/* ── Page header ───────────────────────────────────────────────── */}
            <PageHeader
              title="PROVIDER EVENTS"
              subtitle={
                hasFilter ? 'Filtered results — page ' + page : 'Page ' + page
              }
            >
              <Button
                color="secondary"
                outline
                size="sm"
                onClick={this.load}
                disabled={loading}
                title="Refresh"
              >
                <i
                  className={classnames('fas fa-sync-alt', {
                    'fa-spin': loading,
                  })}
                />
              </Button>
            </PageHeader>

            {/* ── Filter builder ─────────────────────────────────────────────── */}
            <div style={{ marginBottom: 16 }}>
              <FilterBuilder
                key={builderKey}
                fields={PROVIDER_EVENT_FIELDS}
                initialConditions={conditions}
                initialLogicalOp={logicalOp}
                onChange={this.handleFilterChange}
                onSaveFilter={this.handleSaveFilter}
              />
            </div>

            {/* ── No filter: SavedFilters panel as placeholder ───────────────── */}
            {!hasFilter && (
              <FilterPanel
                inline
                resource="provider-events"
                fields={PROVIDER_EVENT_FIELDS}
                activeSavedFilter={activeSavedFilter}
                onApplySavedFilter={this.handleApplySavedFilter}
                onSavedFilterUpdated={this.handleSavedFilterUpdated}
                onDeletedSavedFilter={this.handleDeletedSavedFilter}
              />
            )}

            {/* ── Filter applied: results table ──────────────────────────────── */}
            {hasFilter && (
              <DataTable
                minWidth={600}
                loading={loading}
                page={page}
                total={pages}
                onPageChange={this.setPage}
                showPagination={hasResults}
              >
                <thead className="text-primary">
                  <tr>
                    <th>#</th>
                    <th>Reference</th>
                    <th>Provider Key</th>
                    <th>Event Type</th>
                    <th>Transmissions</th>
                    <th>Status</th>
                    <th>Date</th>
                  </tr>
                </thead>

                {!loading && !hasResults ? (
                  <EmptyState
                    colSpan={7}
                    heading="No provider events match your filters"
                    message="Try adjusting or removing one of the conditions above."
                    actionLabel="Clear filters"
                    onAction={this.clearFilters}
                  />
                ) : (
                  <tbody>
                    {rows.map((t, i) => (
                      <tr
                        key={t.id}
                        onClick={() => this.view(t)}
                        style={{ cursor: 'pointer' }}
                      >
                        <td>{loading ? '—' : i + 1 + (page - 1) * limit}</td>
                        <td>{t.reference}</td>
                        <td>{t.provider_key}</td>
                        <td>{t.event_type}</td>
                        <td style={{ textAlign: 'right' }}>
                          {t.transmission_count}
                        </td>
                        <td>
                          <span
                            className={classnames({
                              badge: true,
                              'badge-success': t.status === 'SUCCESSFUL',
                              'badge-danger': t.status === 'FAILED',
                              'badge-warning': t.status === 'PENDING',
                            })}
                          >
                            {t.status}
                          </span>
                        </td>
                        <td style={{ whiteSpace: 'nowrap' }}>
                          {formatDateTime(t.created_at)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                )}
              </DataTable>
            )}
          </Col>
        </Row>
      </div>
    );
  }
}

export default connect()(List);
